/* eslint-disable react/jsx-no-constructed-context-values */
/*
 * Title: Reconcile In Bulk - ContextAPI
 * Description: State management for bulk stock reconciliation
 */

import { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import ToastifyService from '../../../services/ToastifyService';
import UrlService from '../../../services/UrlService';

const ContextAPI = createContext();

export function useReconcileInBulk() {
    return useContext(ContextAPI);
}

const initialState = {
    bulkData: [],
    loading: false,
    showResults: false,
    filterType: 'all',
    warehouseId: 1,
    warehouses: [],
    suppliers: [],
    manufacturers: [],
    lossReasons: [],
    lossReasonId: null,
    lossRemark: 'Reconcile Bulk',
    lossDate: new Date().toISOString().slice(0, 10),
    rowInputs: {},
    rowStatuses: {},
    showModal: false,
    modalRowData: null,
    executing: false,
    showImportModal: false,
    importPreviewItems: [],
    importExecuting: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_BULK_DATA':
            return { ...state, bulkData: action.payload, showResults: true };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_FILTER_TYPE':
            return { ...state, filterType: action.payload };
        case 'SET_WAREHOUSE_ID':
            return { ...state, warehouseId: action.payload };
        case 'SET_WAREHOUSES':
            return { ...state, warehouses: action.payload };
        case 'SET_SUPPLIERS':
            return { ...state, suppliers: action.payload };
        case 'SET_MANUFACTURERS':
            return { ...state, manufacturers: action.payload };
        case 'SET_LOSS_REASONS':
            return { ...state, lossReasons: action.payload };
        case 'SET_LOSS_REASON_ID':
            return { ...state, lossReasonId: action.payload };
        case 'SET_LOSS_REMARK':
            return { ...state, lossRemark: action.payload };
        case 'SET_LOSS_DATE':
            return { ...state, lossDate: action.payload };
        case 'SET_ROW_INPUT': {
            const { detailId, qty } = action.payload;
            const item = state.bulkData.find(d => d.detail_id === detailId);
            const currentAvailable = item?.available_qty || 0;
            const expectedQty = parseInt(qty, 10) || 0;
            const diff = expectedQty - currentAvailable;
            const type = diff > 0 ? 'gain' : (diff < 0 ? 'loss' : 'equal');
            return {
                ...state,
                rowInputs: { ...state.rowInputs, [detailId]: expectedQty },
                rowStatuses: {
                    ...state.rowStatuses,
                    [detailId]: { ...state.rowStatuses[detailId], type, diff, expectedQty }
                }
            };
        }
        case 'SET_ROW_SIMULATE_RESULT': {
            const { detailId, result } = action.payload;
            return {
                ...state,
                rowStatuses: {
                    ...state.rowStatuses,
                    [detailId]: { ...state.rowStatuses[detailId], ...result }
                }
            };
        }
        case 'OPEN_MODAL':
            return { ...state, showModal: true, modalRowData: action.payload };
        case 'CLOSE_MODAL':
            return { ...state, showModal: false, modalRowData: null };
        case 'SET_MODAL_ADJUSTED_QTY':
            return {
                ...state,
                modalRowData: state.modalRowData ? { ...state.modalRowData, adjustedQty: action.payload } : null
            };
        case 'SET_EXECUTING':
            return { ...state, executing: action.payload };
        case 'OPEN_IMPORT_MODAL':
            return { ...state, showImportModal: true, importPreviewItems: action.payload };
        case 'CLOSE_IMPORT_MODAL':
            return { ...state, showImportModal: false, importPreviewItems: [], importExecuting: false };
        case 'SET_IMPORT_EXECUTING':
            return { ...state, importExecuting: action.payload };
        case 'TOGGLE_IMPORT_ITEM': {
            const { index } = action.payload;
            const updated = [...state.importPreviewItems];
            if (updated[index]) {
                updated[index] = { ...updated[index], checked: !updated[index].checked };
            }
            return { ...state, importPreviewItems: updated };
        }
        case 'TOGGLE_ALL_IMPORT_ITEMS': {
            const checked = action.payload;
            const updated = state.importPreviewItems.map(item =>
                item.status === 'changed' ? { ...item, checked } : item
            );
            return { ...state, importPreviewItems: updated };
        }
        case 'CLEAR_RESULTS':
            return { ...state, bulkData: [], showResults: false, rowInputs: {}, rowStatuses: {}, filterType: 'all' };
        default:
            return state;
    }
}

export function ReconcileInBulkProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const axiosPrivate = useAxiosPrivate();

    // Ref to store current warehouseId for use in callbacks
    const warehouseIdRef = useRef(state.warehouseId);
    warehouseIdRef.current = state.warehouseId;

    const fetchBulkData = useCallback(async (warehouseId, search = '') => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const params = { warehouse_id: warehouseId };
            if (search) params.search = search;
            const response = await axiosPrivate.get(UrlService.reconcileBulkDataUrl(), {
                params
            });
            if (response?.data?.status === 'success') {
                dispatch({ type: 'SET_BULK_DATA', payload: response.data.data });
            } else {
                ToastifyService.errorNotify(response?.data?.message || 'Failed to fetch data');
            }
        } catch (err) {
            if (!err?.response) {
                ToastifyService.errorNotify('Server Not Responding!');
            } else if (err.response?.status === 401) {
                ToastifyService.errorNotify('Unauthorized!');
            } else {
                ToastifyService.errorNotify(err.response?.data?.message || 'Failed to fetch data!');
            }
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [axiosPrivate]);

    const fetchWarehouses = useCallback(async () => {
        try {
            const response = await axiosPrivate.get(UrlService.reconcileBulkWarehousesUrl());
            if (response?.data?.status === 'success') {
                dispatch({ type: 'SET_WAREHOUSES', payload: response.data.data });
            }
        } catch (err) {
            console.error('Failed to fetch warehouses', err);
        }
    }, [axiosPrivate]);

    const fetchSuppliers = useCallback(async () => {
        try {
            const response = await axiosPrivate.get(UrlService.supplierListUrl());
            if (response?.data?.status === 'success') {
                dispatch({ type: 'SET_SUPPLIERS', payload: response.data.data });
            }
        } catch (err) {
            console.error('Failed to fetch suppliers', err);
        }
    }, [axiosPrivate]);

    const fetchManufacturers = useCallback(async () => {
        try {
            const response = await axiosPrivate.get(UrlService.manufacturerListUrl());
            if (response?.data?.status === 'success') {
                dispatch({ type: 'SET_MANUFACTURERS', payload: response.data.data });
            }
        } catch (err) {
            console.error('Failed to fetch manufacturers', err);
        }
    }, [axiosPrivate]);

    const fetchLossReasons = useCallback(async () => {
        try {
            const response = await axiosPrivate.get(UrlService.lossReasonListUrl());
            const reasons = response?.data?.loss_reason_list || [];
            dispatch({ type: 'SET_LOSS_REASONS', payload: reasons });
            const reconcileReason = reasons.find(r => r.title?.toLowerCase() === 'reconcile');
            if (reconcileReason) {
                dispatch({ type: 'SET_LOSS_REASON_ID', payload: reconcileReason.id });
            }
        } catch (err) {
            console.error('Failed to fetch loss reasons', err);
        }
    }, [axiosPrivate]);

    const setLossReasonId = useCallback((id) => {
        dispatch({ type: 'SET_LOSS_REASON_ID', payload: id });
    }, []);

    const setLossRemark = useCallback((val) => {
        dispatch({ type: 'SET_LOSS_REMARK', payload: val });
    }, []);

    const setLossDate = useCallback((val) => {
        dispatch({ type: 'SET_LOSS_DATE', payload: val });
    }, []);

    const handleRowInput = useCallback((detailId, qty) => {
        dispatch({ type: 'SET_ROW_INPUT', payload: { detailId, qty } });
    }, []);

    const runSimulate = useCallback(async (detailId, expectedQty) => {
        try {
            const response = await axiosPrivate.post(UrlService.reconcileBulkSimulateUrl(), {
                items: [{ detail_id: detailId, expected_qty: expectedQty }]
            });
            if (response?.data?.status === 'success' && response.data.items?.[0]) {
                dispatch({ type: 'SET_ROW_SIMULATE_RESULT', payload: { detailId, result: response.data.items[0] } });
                return response.data.items[0];
            }
            return null;
        } catch (err) {
            console.error('Simulate failed', err);
            return null;
        }
    }, [axiosPrivate]);

    const openModal = useCallback(async (rowData) => {
        const detailId = rowData.detail_id;
        const expectedQty = rowData.expected_qty || rowData.available_qty;
        
        // Check if we already have simulate result with changes
        const existingStatus = rowData.status || {};
        let modalPayload = rowData;
        if (!existingStatus.changes || existingStatus.changes.length === 0) {
            // Run simulate to get changes
            try {
                const response = await axiosPrivate.post(UrlService.reconcileBulkSimulateUrl(), {
                    items: [{ detail_id: detailId, expected_qty: expectedQty }]
                });
                if (response?.data?.status === 'success' && response.data.items?.[0]) {
                    const result = response.data.items[0];
                    // Merge simulate result into rowData
                    modalPayload = { ...rowData, ...result, expected_qty: expectedQty };
                }
            } catch (err) {
                console.error('Simulate failed in openModal', err);
            }
        }
        dispatch({ type: 'OPEN_MODAL', payload: modalPayload });
    }, [axiosPrivate]);

    const closeModal = useCallback(() => {
        dispatch({ type: 'CLOSE_MODAL' });
    }, []);

    const setModalAdjustedQty = useCallback((qty) => {
        dispatch({ type: 'SET_MODAL_ADJUSTED_QTY', payload: qty });
    }, []);

    const handleExecute = useCallback(async (detailId, adjustedQty, extraFields = {}) => {
        dispatch({ type: 'SET_EXECUTING', payload: true });
        try {
            const response = await axiosPrivate.post(UrlService.reconcileBulkExecuteUrl(), {
                items: [{ detail_id: detailId, expected_qty: adjustedQty, ...extraFields }]
            });
            if (response?.data?.status === 'success') {
                ToastifyService.saveNotify('Reconciliation executed successfully!');
                dispatch({ type: 'CLOSE_MODAL' });
                // Refresh bulk data to reflect updated available_qty
                await fetchBulkData(warehouseIdRef.current);
                dispatch({ type: 'SET_EXECUTING', payload: false });
                return true;
            }
            ToastifyService.errorNotify(response?.data?.message || 'Execution failed');
        } catch (err) {
            if (!err?.response) {
                ToastifyService.errorNotify('Server Not Responding!');
            } else if (err.response?.status === 401) {
                ToastifyService.errorNotify('Unauthorized!');
            } else if (err.response?.status === 409) {
                ToastifyService.errorNotify(err.response?.data?.message || 'Item is locked by another user');
            } else {
                ToastifyService.errorNotify(err.response?.data?.message || 'Execution Failed!');
            }
        } finally {
            dispatch({ type: 'SET_EXECUTING', payload: false });
        }
        return false;
    }, [axiosPrivate, fetchBulkData]);

    const setFilterType = useCallback((type) => {
        dispatch({ type: 'SET_FILTER_TYPE', payload: type });
    }, []);

    const setWarehouseId = useCallback((id) => {
        dispatch({ type: 'SET_WAREHOUSE_ID', payload: id });
        warehouseIdRef.current = id;
    }, []);

    const clearResults = useCallback(() => {
        dispatch({ type: 'CLEAR_RESULTS' });
    }, []);

    const exportExcel = useCallback(() => {
        import('xlsx').then((XLSX) => {
            const data = state.bulkData || [];
            if (!data.length) {
                ToastifyService.errorNotify('No data to export');
                return;
            }
            const exportRows = data.map(item => ({
                detail_id: item.detail_id,
                Code: item.code_no,
                'Drug Name': item.drug_name,
                'PO No': item.po_no,
                Batch: item.batch_no,
                'Receive Qty': item.receive_qty,
                'Available Qty': item.available_qty,
                'Dispatch Qty': item.dispatch_qty,
            }));
            const ws = XLSX.utils.json_to_sheet(exportRows);
            ws['!cols'] = [
                { wch: 10 }, { wch: 12 }, { wch: 30 }, { wch: 15 },
                { wch: 15 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Reconcile Data');
            XLSX.writeFile(wb, 'reconcile_in_bulk_data.xlsx');
            ToastifyService.saveNotify('Excel exported successfully!');
        });
    }, [state.bulkData]);

    const importExcel = useCallback(async (file) => {
        try {
            const XLSX = await import('xlsx');
            const data = await file.arrayBuffer();
            const wb = XLSX.read(data, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws);

            const items = rows
                .filter(r => r.detail_id)
                .map(r => ({
                    detail_id: r.detail_id,
                    available_qty: r['Available Qty'] ?? r.available_qty ?? 0,
                }));

            if (!items.length) {
                ToastifyService.errorNotify('No valid rows found. Make sure detail_id and Available Qty columns exist.');
                return;
            }

            const response = await axiosPrivate.post(UrlService.reconcileBulkImportPreviewUrl(), {
                warehouse_id: warehouseIdRef.current,
                items,
            });

            if (response?.data?.status === 'success') {
                const previewItems = response.data.items.map(item => ({
                    ...item,
                    checked: item.status === 'changed',
                }));
                dispatch({ type: 'OPEN_IMPORT_MODAL', payload: previewItems });
            } else {
                ToastifyService.errorNotify(response?.data?.message || 'Import preview failed');
            }
        } catch (err) {
            if (!err?.response) {
                ToastifyService.errorNotify('Server Not Responding!');
            } else {
                ToastifyService.errorNotify(err.response?.data?.message || 'Import failed!');
            }
        }
    }, [axiosPrivate]);

    const executeBulk = useCallback(async (checkedItems) => {
        dispatch({ type: 'SET_IMPORT_EXECUTING', payload: true });
        try {
            const payload = checkedItems.map(item => ({
                detail_id: item.detail_id,
                expected_qty: item.new_qty,
            }));
            const response = await axiosPrivate.post(UrlService.reconcileBulkExecuteBulkUrl(), {
                items: payload,
                loss_reason_id: state.lossReasonId,
                loss_remark: state.lossRemark,
                loss_date: state.lossDate,
            });
            if (response?.data?.status === 'success') {
                ToastifyService.saveNotify(response.data.message || 'Bulk reconciliation executed!');
                dispatch({ type: 'CLOSE_IMPORT_MODAL' });
                await fetchBulkData(warehouseIdRef.current);
            } else {
                const msg = response?.data?.message || 'Execution failed';
                const failedItems = response?.data?.results?.filter(r => !r.success) || [];
                if (failedItems.length) {
                    ToastifyService.errorNotify(`${msg} (${failedItems.length} items failed)`);
                } else {
                    ToastifyService.errorNotify(msg);
                }
            }
        } catch (err) {
            if (!err?.response) {
                ToastifyService.errorNotify('Server Not Responding!');
            } else {
                ToastifyService.errorNotify(err.response?.data?.message || 'Execution failed!');
            }
        } finally {
            dispatch({ type: 'SET_IMPORT_EXECUTING', payload: false });
        }
    }, [axiosPrivate, fetchBulkData, state.lossReasonId, state.lossRemark, state.lossDate]);

    const toggleImportItem = useCallback((index) => {
        dispatch({ type: 'TOGGLE_IMPORT_ITEM', payload: { index } });
    }, []);

    const toggleAllImportItems = useCallback((checked) => {
        dispatch({ type: 'TOGGLE_ALL_IMPORT_ITEMS', payload: checked });
    }, []);

    const closeImportModal = useCallback(() => {
        dispatch({ type: 'CLOSE_IMPORT_MODAL' });
    }, []);

    const filteredItems = state.bulkData?.filter((item) => {
        if (state.filterType === 'all') return true;
        const status = state.rowStatuses[item.detail_id]?.type || 'equal';
        return status === state.filterType;
    }) || [];

    return (
        <ContextAPI.Provider
            value={{
                ...state,
                filteredItems,
                dispatch,
                fetchBulkData,
                fetchWarehouses,
                fetchSuppliers,
                fetchManufacturers,
                fetchLossReasons,
                setLossReasonId,
                setLossRemark,
                setLossDate,
                handleRowInput,
                runSimulate,
                openModal,
                closeModal,
                setModalAdjustedQty,
                handleExecute,
                setFilterType,
                setWarehouseId,
                clearResults,
                exportExcel,
                importExcel,
                executeBulk,
                toggleImportItem,
                toggleAllImportItems,
                closeImportModal,
            }}
        >
            {children}
        </ContextAPI.Provider>
    );
}

export default ContextAPI;